import React from "react";
import { FileText } from "lucide-react";

const LAST_UPDATED = "1 April 2026";
const COMPANY = "FindJoy143";
const EMAIL = "findjoy143@gmail.com";

export default function TermsOfService() {
  return (
    <div className="max-w-3xl animate-fade-in" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
          <FileText size={20} className="text-blue-700" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">Terms of Service</h1>
          <p className="text-sm text-gray-500">Last updated: {LAST_UPDATED}</p>
        </div>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
        <p className="text-sm font-bold text-red-800 mb-1">⚠ Important: TaxBuddy is NOT a tax agent</p>
        <p className="text-sm text-red-700">TaxBuddy is a tax categorisation tool only. It does not provide tax advice, legal advice, or financial advice. Nothing in this application constitutes a recommendation to lodge your tax return in any particular way. You must consult a registered tax agent or accountant before lodging.</p>
      </div>

      <div className="bg-white border border-gray-100 rounded-xl p-6 space-y-6 text-sm text-gray-700 leading-relaxed">
        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">1. Acceptance of Terms</h2>
          <p>By creating an account or using TaxBuddy, you agree to these Terms of Service. If you do not agree, do not use the service. These terms form a binding legal agreement between you and {COMPANY}, operating TaxBuddy (contact: <a href={`mailto:${EMAIL}`} className="text-blue-700 underline">{EMAIL}</a>).</p>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">2. Description of Service</h2>
          <p>TaxBuddy is a software-as-a-service (SaaS) platform that helps you:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Upload and parse bank statement PDFs to extract transactions</li>
            <li>Categorise transactions using automated rules and manual review</li>
            <li>Generate spreadsheet reports to share with your accountant</li>
            <li>Maintain a digital record of expenses throughout the financial year</li>
          </ul>
          <p className="mt-3 font-semibold">TaxBuddy does NOT:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1 text-red-700">
            <li>Provide tax advice, financial advice, or legal advice</li>
            <li>Lodge your tax return or interact with the ATO on your behalf</li>
            <li>Guarantee the accuracy of automated categorisation</li>
            <li>Access your bank accounts directly</li>
            <li>Act as a registered tax agent under the <em>Tax Agent Services Act 2009</em></li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">3. Disclaimer of Tax and Financial Advice</h2>
          <p className="font-bold text-gray-900">This is the most important section. Please read it carefully.</p>
          <p className="mt-2">All categorisation, deductibility flags, ATO labels, and "potentially deductible" indicators in TaxBuddy are automated estimates based on pattern matching. They are provided for informational and organisational purposes only.</p>
          <p className="mt-2">These estimates:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>May be incorrect, incomplete, or inapplicable to your situation</li>
            <li>Do not constitute advice from a registered tax agent</li>
            <li>Do not account for your specific circumstances, income, employment type, or applicable tax offsets</li>
            <li>Must be reviewed and confirmed by a registered tax agent before you rely on them</li>
          </ul>
          <p className="mt-3">{COMPANY} accepts no liability for any tax liability, penalties, interest, or other consequence arising from your reliance on TaxBuddy outputs without independent professional verification.</p>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">4. User Responsibilities</h2>
          <p>You agree to:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Provide accurate information when creating your account</li>
            <li>Keep your login credentials secure and not share them</li>
            <li>Only upload bank statements for accounts you own or are authorised to access</li>
            <li>Not use TaxBuddy for illegal purposes or to process funds from illegal activities</li>
            <li>Not attempt to reverse-engineer, copy, or exploit the TaxBuddy platform</li>
            <li>Review automated categorisations before relying on them for tax purposes</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">5. Subscription and Billing</h2>
          <p><strong>Free plan:</strong> Allows upload of 1 statement per financial year and basic transaction review. No credit card required.</p>
          <p className="mt-2"><strong>Pro Monthly ($9/month):</strong> Unlimited statements, Excel export, Quick Sort mode, and priority support. Billed monthly in advance.</p>
          <p className="mt-2"><strong>Pro Annual ($79/year):</strong> Everything in Pro Monthly, billed annually. $6.58/month equivalent.</p>
          <p className="mt-2"><strong>Billing:</strong> Payments are processed by Stripe. By subscribing you authorise recurring charges to your payment method.</p>
          <p className="mt-2"><strong>Cancellation — Monthly:</strong> You may cancel at any time. Pro access continues until the end of the current billing period. No refund for the current month.</p>
          <p className="mt-2"><strong>Cancellation — Annual:</strong> You may cancel at any time. If you cancel within 14 days of purchase, you receive a full automatic refund. After 14 days, Pro access continues until the end of your annual period — no partial refund is issued.</p>
          <p className="mt-2"><strong>Australian Consumer Law:</strong> Nothing in these terms excludes rights under the Australian Consumer Law that cannot be excluded by agreement.</p>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">6. Data and Intellectual Property</h2>
          <p><strong>Your data:</strong> You own your transaction data. We hold it under licence solely to provide the service. You can export or delete it at any time.</p>
          <p className="mt-2"><strong>Our platform:</strong> TaxBuddy's software, categorisation logic, design, and branding are owned by {COMPANY}. You may not copy, reproduce, or distribute any part of the platform.</p>
          <p className="mt-2"><strong>Feedback:</strong> Any suggestions or feedback you provide may be used to improve the service without compensation or attribution.</p>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">7. Limitation of Liability</h2>
          <p>To the maximum extent permitted by Australian law, {COMPANY}'s total liability to you for any claim arising from your use of TaxBuddy is limited to the amount you paid for the service in the 12 months preceding the claim.</p>
          <p className="mt-2">We are not liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, loss of data, tax penalties, or ATO assessments.</p>
          <p className="mt-2">Nothing in these terms excludes rights under the Australian Consumer Law that cannot be excluded.</p>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">8. Availability and Changes</h2>
          <p>We aim for high availability but do not guarantee uninterrupted service. We may modify, suspend, or discontinue features with reasonable notice. We reserve the right to update these terms and will notify you 14 days before material changes take effect.</p>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">9. Governing Law</h2>
          <p>These terms are governed by the laws of New South Wales, Australia. Any disputes will be resolved in the courts of New South Wales.</p>
        </section>

        <section>
          <h2 className="font-bold text-gray-900 text-base mb-3">10. Contact</h2>
          <p>{COMPANY} — operating TaxBuddy<br/>
          Email: <a href={`mailto:${EMAIL}`} className="text-blue-700 underline">{EMAIL}</a></p>
        </section>
      </div>
    </div>
  );
}
